import { RepeatIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Stack,
} from '@chakra-ui/react';
import React from 'react';
import Preview from 'shared/src/details/PreviewSection';
import ActionMenu from 'web-react-ui/src/components/actionMenu/ActionMenu';
import Post from 'shared/src/interfaces/Post.interface';
import PageHeader from 'web-react-ui/src/chakra/page/PageHeader';
import DateTimeField from 'web-react-ui/src/reactFinalForm/fields/dateTimeField/DateTimeField';
import SimpleForm2 from 'web-react-ui/src/reactFinalForm/SimpleForm2';
import TextArea from 'web-react-ui/src/reactFinalForm/fields/TextArea';
import {
  compose,
  required,
  min,
  replaceMessage,
} from 'web-react-ui/src/reactFinalForm/validators';
import {
  useForm,
  useFormState,
} from 'web-react-ui/src/reactFinalForm';
import FieldLabel from 'web-react-ui/src/reactFinalForm/fields/FieldLabel';
import FormError from 'web-react-ui/src/reactFinalForm/FormError';

type ActionType = {
  label: string;
  action?: () => void;
  icon?: string;
  hidden: boolean;
  negative?: boolean;
  positive?: boolean;
  extraProps?: Record<string, unknown>;
};

type PostEditProps = {
  onSave: ({ message, scheduledDate }: { message: string, scheduledDate: string }) => void;
  post: Post & { pageTitle: string };
  actions: ActionType[];
  admin?: boolean;
};

const PostEdit = ({ post, actions, admin }: Omit<PostEditProps, 'onSave'>) => {
  const form = useForm();
  // useFormState instead of form.getState because the former subscribes to form state updates
  // which is necessary for the <Preview /> to update in real-time
  const formState = useFormState();
  const resetMessage = () => {
    form.change('output.message', formState.initialValues.output.message);
  };

  const canEditScheduledDate = post.state === 'scheduled';
  const canEditMessage = post.contentType === 'post';
  const willGoLive = new Date(formState.values.scheduledDate) <= Date.now();

  return (
    <Box
      as="div"
      w="auto"
      mx={{ base: '0.5rem', md: '4rem' }}
      mb="4rem"
      className="_mainContainer"
      color="gray.800"
    >
      <PageHeader controls={<ActionMenu actions={actions} />}>
        {post.pageTitle}
      </PageHeader>
      <Flex
        wrap={{ base: 'wrap', md: 'nowrap' }}
        direction={{ base: 'column', lg: 'row' }}
        gap="3em"
        minW="1/2"
        className="_postDetailContainer"
        justifyContent="center"
      >
        <Flex
          direction="column"
          gap="1.5em"
          w="2/3"
          grow={2}
          alignItems="start"
        >
          <FormError />
          {canEditScheduledDate && (
            <>
              <DateTimeField
                name="scheduledDate"
                label="Scheduled Date"
                minDate={new Date()}
              />
              {
                willGoLive && (
                  <Alert status="info">
                    <AlertIcon />
                    This will be posted immediately on Save
                  </Alert>
                )
              }
            </>
          )}
          {canEditMessage && (
            <Stack w="100%">
              <Flex w="100%" justifyContent="space-between">
                <FieldLabel>Post Description</FieldLabel>
                <RepeatIcon
                  aria-label="Restore message"
                  color="gray.300"
                  w="6"
                  h="6"
                  className="repeat-icon"
                  onClick={resetMessage}
                />
              </Flex>
              <TextArea
                chakra
                wrapLabel
                name="output.message"
                placeholder="Enter a short post description here"
                h="10lh"
                size="lg"
                validate={replaceMessage(
                  compose(required, min(10)),
                  () => 'Post descriptions cannot remain blank. Please provide at least 10 characters.',
                )}
              />
            </Stack>
          )}
        </Flex>
        <Preview post={formState.values} />
      </Flex>
    </Box>
  );
};

const PostEditWrapper = ({ post, onSave, actions, admin }: PostEditProps) => {
  const saveAction = {
    label: 'Save',
    hidden: false,
    positive: true,
    extraProps: {
      type: 'submit',
    },
  };
  const handleSubmit = async (postUpdates: Post, form: any) => {
    const message = postUpdates.output.message || undefined; // can't send empty message

    let scheduledDate;
    const initialValues = form.getState().initialValues;
    if (postUpdates.scheduledDate !== initialValues.scheduledDate) {
      scheduledDate = postUpdates.scheduledDate;
      // The date picker is restricted to the future _at the time it was opened_.
      // So if the date is slightly in the past, it's because the user didn't submit immediately.
      // So we clamp the time to "now"
      if (new Date(postUpdates.scheduledDate).valueOf() < Date.now()) {
        scheduledDate = new Date().toISOString();
      }
    }

    return onSave({
      message,
      scheduledDate,
    });
  };
  const newActions = [saveAction, ...(actions || [])];
  return (
    <SimpleForm2 onSubmit={handleSubmit} initialValues={post}>
      <PostEdit post={post} actions={newActions} admin={admin} />
    </SimpleForm2>
  );
};

export default PostEditWrapper;
